import { ReactNode, useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { Column } from 'components/Blocks';

import { SingleComment } from './SingleComment';
import { CommentInput } from './CommentInput';
import { CommentHistoryModal, HistoryCommentTarget } from './CommentHistoryModal';

import { useToggleV2, useUser } from 'hooks';

import { addCommentContent } from './utils';

import { BaseComment } from './types';
import { cn } from 'styles';

interface CommentsProps<Comment extends BaseComment> {
  comments: Comment[];

  onCreate?: (newComment: string, files: File[]) => Promise<Comment | boolean | void>;

  onEdit?: (text: string, comment: Comment) => Promise<void | Comment>;

  historyTarget?: keyof HistoryCommentTarget;

  inputBellowTitle?: boolean;
  addTitle?: boolean;
  canEdit: boolean;
  allowFiles?: boolean;

  className?: string;

  /**
   * Add content below the regular comment box
   */
  getExtraContent?: (comment: Comment) => ReactNode;
}

export function Comments<Comment extends BaseComment>({
  canEdit,
  onCreate,
  historyTarget,
  comments,
  onEdit,
  inputBellowTitle = false,
  addTitle = true,
  className,
  getExtraContent,
}: CommentsProps<Comment>) {
  const { t } = useLang();
  const loggedUser = useUser();

  const [comment, setComment] = useState<Comment | null>(null);

  const [loadingCreation, creationLoadingHandlers] = useToggleV2();

  const createComment = async (newComment: string, files: File[]) => {
    if (!newComment) return;

    creationLoadingHandlers.turnOn();

    try {
      const comment = await onCreate?.(newComment, files);

      creationLoadingHandlers.turnOff();

      if (!comment) return;

      return true;
    } catch {
      creationLoadingHandlers.turnOff();
    }
  };

  const parsedComments = useMemo(() => {
    return comments.map(addCommentContent) || [];
  }, [comments]);

  return (
    <>
      {!!historyTarget && (
        <CommentHistoryModal
          historyTarget={historyTarget}
          close={() => setComment(null)}
          comment={comment}
        />
      )}

      <Column className={cn('rounded-md border p-4', className)}>
        {!inputBellowTitle && canEdit && (
          <CommentInput
            saveComment={createComment}
            disabled={loadingCreation}
            loading={loadingCreation}
          />
        )}

        <Column>
          {!!comments.length && addTitle && (
            <p className="text-sm font-medium">
              {t('comments')} ({comments.length})
            </p>
          )}

          {inputBellowTitle && canEdit && (
            <CommentInput
              saveComment={createComment}
              disabled={loadingCreation}
              loading={loadingCreation}
            />
          )}

          {!comments.length ? (
            <p className="text-center text-sm italic">{t('no-comments-found')}</p>
          ) : (
            parsedComments.map((comment) => (
              <SingleComment
                canEdit={canEdit && loggedUser.id === comment.user.id}
                onEdit={onEdit}
                key={`normal-${comment.id}`}
                comment={comment}
                viewHistory={() => setComment(comment)}
                getExtraContent={getExtraContent}
              />
            ))
          )}
        </Column>
      </Column>
    </>
  );
}
